// src/pages/HRMSSoftware.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";
import Flag from 'react-country-flag';
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const COUNTRIES = [
  {
    code: "IN",
    name: "India",
    dial: "+91",
    iso: "IN",
    min: 10,
    max: 10,
  },
  {
    code: "US",
    name: "United States",
    dial: "+1",
    iso: "US",
    min: 10,
    max: 10,
  },
  {
    code: "GB",
    name: "United Kingdom",
    dial: "+44",
    iso: "GB",
    min: 10,
    max: 11,
  },
  {
    code: "AU",
    name: "Australia",
    dial: "+61",
    iso: "AU",
    min: 9,
    max: 9,
  },
  {
    code: "AE",
    name: "UAE",
    dial: "+971",
    iso: "AE",
    min: 9,
    max: 9,
  },
];

/* ------------------- CONTACT FORM ------------------- */
const ContactForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    countrycode: "",
    phone: "",
    location: "",
    no_of_employees: "",
    no_of_branches: "",
    product_taking_time: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  
  // NEW: Country dropdown states
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');

  const filteredCountries = COUNTRIES.filter(country =>
    country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    country.dial.toLowerCase().includes(countrySearch.toLowerCase())
  );

  const handleCountrySelect = (dialCode, country) => {
    setSelectedCountry(country);
    setForm(prev => ({ ...prev, countrycode: dialCode, phone: "" }));
    setIsCountryOpen(false);
    setCountrySearch('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Name validation - only letters and spaces
    if (name === "name") {
      const nameRegex = /^[a-zA-Z\s]*$/;
      if (!nameRegex.test(value)) return;
    }
    
    setForm({ ...form, [name]: value });
  };

  const validate = () => {
    if (!form.name?.trim()) return "Name is required";
    if (!form.email?.trim()) return "Email is required";

    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(form.email)) return "Enter a valid email address";

    if (!selectedCountry) return "Please select a country";
    if (!form.phone?.trim()) return "Phone number is required";

    if (
      form.phone.length < selectedCountry.min ||
      form.phone.length > selectedCountry.max
    ) {
      return `Phone number must be between ${selectedCountry.min} and ${selectedCountry.max} digits for ${selectedCountry.name}`;
    }

    if (!form.location?.trim()) return "Location is required";

    if (
      !form.no_of_employees ||
      isNaN(form.no_of_employees) ||
      parseInt(form.no_of_employees) < 1
    ) {
      return "Enter valid number of employees (minimum 1)";
    }

    if (
      !form.no_of_branches ||
      isNaN(form.no_of_branches) ||
      parseInt(form.no_of_branches) < 0
    ) {
      return "Enter valid number of branches (minimum 0)";
    }

    if (!form.product_taking_time?.trim())
      return "Please enter when you plan to start";

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const err = validate();
    if (err) {
      alert(err);
      return;
    }

    setSubmitting(true);

    // Simulate API call
    setTimeout(() => {
      setSubmitting(false);
      setShowSuccessPopup(true);
      setForm({
        name: "",
        email: "",
        countrycode: "",
        phone: "",
        location: "",
        no_of_employees: "",
        no_of_branches: "",
        product_taking_time: "",
      });
    }, 1200);
  };

  const closePopup = () => {
    setShowSuccessPopup(false);
  };

  useEffect(() => {
    if (showSuccessPopup) {
      const timer = setTimeout(() => {
        setShowSuccessPopup(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessPopup]);

  if (showSuccessPopup) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl relative">
          <button
            onClick={closePopup}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-xl font-bold p-2 hover:bg-gray-100 rounded-full transition-all w-10 h-10 flex items-center justify-center"
          >
            ×
          </button>
          
          <div className="text-center pt-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-green-600 mb-2">Success!</h3>
            <p className="text-gray-700 mb-6">Thank you! We will contact you soon.</p>
            <p className="text-xs text-gray-500">You can request another demo now.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-8 max-w-2xl mx-auto bg-white p-8 rounded-2xl shadow-lg"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Full Name <span className="text-red-500">*</span></label>
          <input
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            placeholder="Enter your full name"
            name="name"
            value={form.name}
            onChange={handleChange}
            maxLength={50}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Email Address <span className="text-red-500">*</span></label>
          <input
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            placeholder="Enter your email address"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            required
          />
        </div>

        {/* NEW: Country Flag Dropdown with Search */}
        <div className="relative md:col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Country <span className="text-red-500">*</span></label>
          <button
            type="button"
            onClick={() => setIsCountryOpen(!isCountryOpen)}
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none bg-white flex items-center justify-between hover:border-indigo-400 transition-colors"
          >
            <span className="flex items-center gap-3 truncate flex-1">
              {selectedCountry ? (
                <>
                  <Flag 
                    countryCode={selectedCountry.iso} 
                    svg 
                    className="w-5 h-3.5 flex-shrink-0" 
                  />
                  <span className="truncate font-medium">
                    {selectedCountry.name}
                  </span>
                  <span className="text-sm opacity-70">
                    {selectedCountry.dial}
                  </span>
                </>
              ) : (
                <span className="text-gray-500">Select Country *</span>
              )}
            </span>
            <svg 
              className={`w-5 h-5 transition-transform ${isCountryOpen ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {isCountryOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-72 overflow-hidden top-full left-0">
              <input
                type="text"
                value={countrySearch}
                onChange={(e) => setCountrySearch(e.target.value)}
                placeholder="Search country or code..."
                className="w-full p-3 border-b border-gray-100 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 rounded-t-xl"
                autoFocus
              />
              <div className="max-h-60 overflow-y-auto">
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((country) => (
                    <button
                      key={country.code}
                      type="button"
                      onClick={() => handleCountrySelect(country.dial, country)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition-colors"
                    >
                      <Flag 
                        countryCode={country.iso} 
                        svg 
                        className="w-5 h-3.5 flex-shrink-0" 
                      />
                      <span className="flex-1 truncate font-medium">{country.name}</span>
                      <span className="text-sm opacity-70">{country.dial}</span>
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-6 text-center text-sm text-gray-500">
                    No countries found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Phone Number <span className="text-red-500">*</span></label>
          <input
            type="tel"
            placeholder={
              selectedCountry
                ? `Enter phone number (${selectedCountry.min}-${selectedCountry.max} digits)`
                : "Enter phone number"
            }
            name="phone"
            value={form.phone}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "");
              if (!selectedCountry) return;
              if (value.length <= selectedCountry.max) {
                setForm({ ...form, phone: value });
              }
            }}
            required
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Location <span className="text-red-500">*</span></label>
          <input
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            placeholder="Enter your city, country"
            name="location"
            value={form.location}
            maxLength={60}
            onChange={handleChange}
            required
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Number of Employees <span className="text-red-500">*</span></label>
          <input
            type="number"
            min="1"
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            placeholder="Enter number of employees"
            name="no_of_employees"
            value={form.no_of_employees}
            onChange={(e) => {
              const value = e.target.value;
              if (value.toString().length <= 10) {
                handleChange(e);
              }
            }}
            required
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Number of Branches <span className="text-red-500">*</span></label>
          <input
            type="number"
            min="0"
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            placeholder="Enter number of branches (0 if none)"
            name="no_of_branches"
            value={form.no_of_branches}
            onChange={(e) => {
              const value = e.target.value;
              if (value.toString().length <= 10) {
                handleChange(e);
              }
            }}
            required
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2 text-left">Planned Start Date <span className="text-red-500">*</span></label>
          <input
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            placeholder="When do you plan to start using the HRMS?"
            name="product_taking_time"
            value={form.product_taking_time}
            maxLength={60}
            onChange={handleChange}
            required
          />
        </div>
      </div>

      <button
        className="w-full py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition disabled:opacity-60"
        disabled={submitting}
      >
        {submitting ? "Submitting..." : "Request Demo"}
      </button>
    </form>
  );
};

/* ------------------- MAIN PAGE ------------------- */
const HRMSSoftware = () => {
  const videoRef = useRef(null);
  const audioUnlockedRef = useRef(false);

  const SCROLL_SHOW_THRESHOLD = 300;
  
  const [showScroll, setShowScroll] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(true);
  
  const scrollTimeoutRef = useRef(null);
  
  const RADIUS = 49;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
  const dashOffset = CIRCUMFERENCE - (scrollProgress / 100) * CIRCUMFERENCE;
  
  const onScroll = useCallback(() => {
    const sc = window.scrollY || window.pageYOffset;
    setShowScroll(sc > SCROLL_SHOW_THRESHOLD);

    const doc = document.documentElement;
    const totalScroll = Math.max(0, doc.scrollHeight - doc.clientHeight);
    const progress = totalScroll > 0 ? (sc / totalScroll) * 100 : 0;

    setScrollProgress(Math.min(100, Math.max(0, progress)));
    setIsScrolling(true);

    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);

    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
      scrollTimeoutRef.current = null;
    }, 250);
  }, []);
  
  // ────────────────────────────────────────────────
  // FIXED: Reliable scroll to top on every mount / refresh
  // ────────────────────────────────────────────────
  useEffect(() => {
    // Prevent browser from restoring previous scroll position
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    // Force scroll to top immediately
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

    // Safety nets for stubborn browsers / mobile / slow rendering
    const forceScroll = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    };

    const t1 = setTimeout(forceScroll, 0);
    const t2 = setTimeout(forceScroll, 50);
    const t3 = setTimeout(forceScroll, 150);

    // Also run on every scroll event just in case (very early)
    const earlyScrollHandler = () => {
      if (window.scrollY > 10) {
        forceScroll();
      }
    };
    window.addEventListener('scroll', earlyScrollHandler, { once: true, passive: true });

    // Main scroll listener
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener('scroll', earlyScrollHandler);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      // Optional: reset to auto when leaving (not always necessary)
      // if ('scrollRestoration' in window.history) {
      //   window.history.scrollRestoration = 'auto';
      // }
    };
  }, [onScroll]);
  
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      scrollToTop();
    }
  };

  // Video audio control
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Always start muted
    video.muted = true;
    video.volume = 0;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(video);

    return () => observer.disconnect();
  }, []);

  // const handleVideoInteraction = (e) => {
  //   const video = videoRef.current;
  //   if (!video || audioUnlockedRef.current) return;

  //   video.muted = false;
  //   video.volume = 0.7;
  //   audioUnlockedRef.current = true;
  //   setIsVideoMuted(false);
  // };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isVideoMuted) {
      video.muted = false;
      video.volume = 0.7;
      setIsVideoMuted(false);
    } else {
      video.muted = true;
      video.volume = 0;
      setIsVideoMuted(true);
    }
  };

  return (
    <>
      <Helmet>
        <title>HRMS Software | Human Resource Management System</title>
        <meta
          name="description"
          content="Full-featured HRMS platform for payroll, attendance, onboarding, and performance."
        />
      </Helmet>
      <Navbar />

      {/* Hero Section */}
      <section className="relative w-full bg-white px-6 mb-10">
        <div className="absolute top-0 right-0 w-[420px] h-[120px] bg-[#cae9ff] rounded-bl-full opacity-70 -z-10"></div>

        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <img src="/img/hrms103.png" alt="Logo" className="w-28 mt-8 mb-6" />
            <p className="text-sm tracking-wide font-semibold text-[#2A4546] mb-2">
              Human Resource Management System 
            </p>

            <h1 className="text-3xl md:text-4xl font-bold text-[#2A4546] leading-tight mb-[38px]">
              For Today's Modern Workplaces
            </h1>

            <p className="text-[#2A4546] mt-4 leading-relaxed mb-[128px]">
              A modern Human Resource Management System designed to streamline
              recruitment, attendance, payroll and employee records, delivering
              secure, seamless and fully automated HR operations for today's
              workplaces.
            </p>
          </div>

          <div className="-mt-20 sm:mt-0 sm:mb-[-32px]">
            <img
              src="/img/hrmsnew.png"
              alt="HRMS Dashboard"
              className="w-full drop-shadow-lg "
            />
          </div>
        </div>
      </section>

      {/* Video Section */}
<section className="w-full py-4 px-6 md:px-0 bg-gray-50">
  <div className="max-w-7xl mx-auto">
    <div className="max-w-3xl mx-auto mb-10 text-center">
      <h2 className="text-3xl font-bold text-[#063852]">
        HR Management Made Simple
      </h2>
      <p className="text-gray-600 mt-3">
        Explore how our HR Management System supports day-to-day HR tasks,
        reduces manual work, and keeps teams organised—helping modern
        workplaces manage people, processes, and productivity more efficiently.
      </p>
    </div>

    <div className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl">
      <video
        ref={videoRef}
        src="/video/Shrava 360 NEW (3).mp4"
        className="w-full h-auto max-h-[220px] sm:max-h-[360px] md:max-h-none block"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        controls
      >
        Your browser does not support HTML5 video.
      </video>
    </div>
  </div>
</section>


      {/* Benefits Section */}
      <section className="relative bg-[#0f0d1f] py-24 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 px-6 relative z-10">
          <div className="flex flex-col gap-6">
            <img src="/img/hrms2.png" className="rounded-xl shadow-2xl" alt="HRMS Dashboard" />
            <img src="/img/hrms1.png" className="rounded-xl shadow-xl" alt="Preview" />
          </div>

          <div className="">
            <h2 className="text-4xl font-extrabold mb-6">
              Benefits of a Human Resource Management System
            </h2>
            <p className="text-xl text-gray-300 mb-12">
              Streamline HR, empower employees and drive organisational success
            </p>

            <div className="space-y-10">
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center text-2xl font-bold">
                  1
                </div>
                <div>
                  <h3 className="text-2xl font-semibold mb-2">
                    Hiring & Onboarding
                  </h3>
                  <p className="text-gray-400">
                    Automate recruitment and onboarding to ensure faster and
                    smoother employee integration
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center text-2xl font-bold">
                  2
                </div>
                <div>
                  <h3 className="text-2xl font-semibold mb-2">
                    Attendance & Leave
                  </h3>
                  <p className="text-gray-400">
                    Track attendance and leave requests to reduce daily
                    administrative workload efficiently
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center text-2xl font-bold">
                  3
                </div>
                <div>
                  <h3 className="text-2xl font-semibold mb-2">
                    Payroll & Compensation
                  </h3>
                  <p className="text-gray-400">
                    Process salaries, bonuses, and taxes accurately for timely
                    employee payments always
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center text-2xl font-bold">
                  4.
                </div>
                <div>
                  <h3 className="text-2xl font-semibold mb-2">
                    Employee Data & Performance
                  </h3>
                  <p className="text-gray-400">
                    Manage records securely and monitor employee performance to
                    support consistent growth
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative bg-white pt-20 pb-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight">
              Complete HRMS Features for Efficient HR Operations
            </h2>
            <p className="mt-6 text-xl md:text-2xl text-gray-700 font-medium max-w-5xl mx-auto">
              A modular and advanced HRMS platform designed to streamline
              recruitment, attendance, payroll and employee records, delivering
              secure, seamless and fully automated HR operations across your
              organization.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div className="space-y-6">
              <details className="group">
                <summary className="flex justify-between items-center py-6 px-8 bg-white border border-gray-200 rounded-2xl cursor-pointer list-none hover:bg-gray-50 transition">
                  <span className="text-2xl lg:text-1xl font-bold text-gray-800">
                    Employee Login & Attendance Tracking
                  </span>
                  <svg className="w-7 h-7 text-gray-500 transition-transform duration-300 group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-8 pb-8 pt-4">
                  <p className="text-lg text-gray-600 leading-relaxed">
                    Secure login, role-based access, punch-in/out and
                    auto-alerts ensures accurate attendance and workforce
                    discipline.
                  </p>
                </div>
              </details>

              <details className="group">
                <summary className="flex justify-between items-center py-6 px-8 bg-white border border-gray-200 rounded-2xl cursor-pointer list-none hover:bg-gray-50 transition">
                  <span className="text-2xl lg:text-1xl font-bold text-gray-800">
                    Automated Payslips & Employee Profiles
                  </span>
                  <svg className="w-7 h-7 text-gray-500 transition-transform duration-300 group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-8 pb-8 pt-4">
                  <p className="text-lg text-gray-600 leading-relaxed">
                    Auto-generated salary slips, bonuses, tax deductions and
                    centralised records saves HR time and reduces errors.
                  </p>
                </div>
              </details>

              <details className="group">
                <summary className="flex justify-between items-center py-6 px-8 bg-white border border-gray-200 rounded-2xl cursor-pointer list-none hover:bg-gray-50 transition">
                  <span className="text-2xl lg:text-1xl font-bold text-gray-800">
                    Recruitment & Onboarding
                  </span>
                  <svg className="w-7 h-7 text-gray-500 transition-transform duration-300 group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-8 pb-8 pt-4">
                  <p className="text-lg text-gray-600 leading-relaxed">
                    Job posting, candidate screening, automated offer letters
                    and onboarding checklists ensures a smooth hiring process.
                  </p>
                </div>
              </details>
            </div>

            <div className="relative w-full flex justify-center lg:justify-end">
              <div className="relative z-20 w-[90%] rounded-3xl overflow-hidden shadow-2xl">
                <img src="/img/hrms4.png" className="w-full h-full object-cover" alt="HRMS Feature 2" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-10 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-gray-800">Get In Touch</h2>
          <p className="mt-4 text-gray-600 text-lg max-w-2xl mx-auto">
            Ready to automate your HR? Request a free product demo today.
          </p>
          <ContactForm />
        </div>
      </section>

      <Footer />

      {/* Scroll to Top */}
      {showScroll && (
        <div
          onClick={scrollToTop}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
          className="scroll-top fixed bottom-6 right-6 z-50 cursor-pointer 
          flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r 
          from-blue-500 to-blue-700 shadow-xl hover:scale-110 transition-all"
        >
          <svg className="progress-circle -rotate-90 w-12 h-12 absolute" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r={RADIUS} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="4" />
            <circle
              cx="50"
              cy="50"
              r={RADIUS}
              fill="none"
              stroke="white"
              strokeWidth="4"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              strokeLinecap="round"
            />
          </svg>

          <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white relative z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </div>
      )}
    </>
  );
};

export default function Shrava360() {
  return <HRMSSoftware />;
}